// TeacherPdfUploader.js
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

// ✅ Prototype dropdown options (later replace with Azure Table fetch)
const YEAR_OPTIONS = [
  { value: "2026", label: "2026" },
  { value: "2025", label: "2025" },
  { value: "2024", label: "2024" },
];

const TYPE_OF_WRITING_OPTIONS = [
  { value: "situational writing", label: "Situational Writing" },
  { value: "composition", label: "Continuous Writing" },
];

const EXAM_LEVEL_OPTIONS = [
  { value: "psle", label: "Primary (PSLE)" },
  { value: "o-level", label: "Secondary (O-Level)" },
  { value: "na-level", label: "Secondary NA (Normal Academic)" },
  { value: "nt-level", label: "Secondary NT (Normal Technical)" },
];

const SHOW_DEBUG_JSON = false; // true only during development (manually change to true for Sandbox uploads. default is false)

function TeacherPdfUploader({ session }) {
  // -----------------------------
  // Auth context (from session)
  // -----------------------------
  const schoolId = session?.school_id || "";
  const teacherId = session?.teacher_id || "";
  const teacherEmail = session?.teacher_email || "";

  // -----------------------------
  // Form selection states
  // -----------------------------
  const [year, setYear] = useState("");
  const [classId, setClassId] = useState("");
  const [typeOfWriting, setTypeOfWriting] = useState("situational writing");
  const [examLevel, setExamLevel] = useState("psle");
  const [activeUploadTab, setActiveUploadTab] = useState("writing");

  // -----------------------------
  // Additional grading guidelines (teacher input)
  // -----------------------------
  const [customGuidelines, setCustomGuidelines] = useState("");
  const [guidelinesStatus, setGuidelinesStatus] = useState("");
  const [guidelinesSaving, setGuidelinesSaving] = useState(false);
  const [oralTopicTitle, setOralTopicTitle] = useState("");
  const [oralPictureStimulus, setOralPictureStimulus] = useState("");
  const [oralPictureFile, setOralPictureFile] = useState(null);
  const [oralQuestion1, setOralQuestion1] = useState("");
  const [oralQuestion2, setOralQuestion2] = useState("");
  const [oralQuestion3, setOralQuestion3] = useState("");
  const [oralGradingGeneral, setOralGradingGeneral] = useState("");
  const [oralGradingQuestion1, setOralGradingQuestion1] = useState("");
  const [oralGradingQuestion2, setOralGradingQuestion2] = useState("");
  const [oralGradingQuestion3, setOralGradingQuestion3] = useState("");
  const [oralTopicStatus, setOralTopicStatus] = useState("");
  const [oralTopicUploading, setOralTopicUploading] = useState(false);
  const [existingOralTopics, setExistingOralTopics] = useState([]);
  const [existingOralTopicsLoading, setExistingOralTopicsLoading] = useState(false);
  const [existingOralTopicsStatus, setExistingOralTopicsStatus] = useState("");
  const [deletingOralTopicId, setDeletingOralTopicId] = useState("");

  // -----------------------------
  // Class dropdown (fetched from backend)
  // -----------------------------
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState("");

  // -----------------------------
  // Add class (no delete)
  // -----------------------------
  const [newStudentClass, setNewStudentClass] = useState("");
  const [addClassLoading, setAddClassLoading] = useState(false);
  const [addClassStatus, setAddClassStatus] = useState("");
  const [addClassError, setAddClassError] = useState("");

  // -----------------------------
  // Upload & job polling states
  // -----------------------------
  const [selectedFile, setSelectedFile] = useState(null);
  const [status, setStatus] = useState("");
  const [jobId, setJobId] = useState("");
  const [jobStatus, setJobStatus] = useState("");
  const [jobResult, setJobResult] = useState(null);
  const pollingRef = useRef(null);

  // -----------------------------
  // Current file card states
  // -----------------------------
  const [currentFile, setCurrentFile] = useState(null);
  const [currentFileLoading, setCurrentFileLoading] = useState(false);
  const [removeStatus, setRemoveStatus] = useState("");
  const [loadError, setLoadError] = useState("");

  // -----------------------------
  // Class verification code states
  // -----------------------------
  const [classConfig, setClassConfig] = useState(null);
  const [classConfigLoading, setClassConfigLoading] = useState(false);
  const [classConfigError, setClassConfigError] = useState("");
  const [rotateStatus, setRotateStatus] = useState("");

  // -----------------------------
  // Defaults
  // -----------------------------
  useEffect(() => {
    if (!year) setYear(new Date().getFullYear().toString());
  }, [year]);

  // -----------------------------
  // Helpers
  // -----------------------------
  const clearPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const formatUploadedAt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat("en-SG", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  };

  const tabButtonStyle = (active) => ({
    padding: "10px 14px",
    borderRadius: 8,
    border: "1px solid #ddd",
    cursor: "pointer",
    fontWeight: 700,
    background: active ? "#16a34a" : "#f3f4f6",
    color: active ? "#fff" : "#111827",
  });

  const primaryActionButtonStyle = {
    padding: "10px 18px",
    backgroundColor: "#3F51B5",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontWeight: 700,
    cursor: "pointer",
  };

  const handlePrimaryButtonMouseOver = (e) => {
    e.target.style.backgroundColor = "#33429a";
  };

  const handlePrimaryButtonMouseOut = (e) => {
    e.target.style.backgroundColor = "#3F51B5";
  };

  // -----------------------------
  // GET: classes for dropdown
  // -----------------------------
  const fetchClasses = async () => {
    setClassesError("");
    setClasses([]);

    if (!schoolId || !teacherId || !year) return;

    setClassesLoading(true);
    try {
      const res = await axios.get(`/api/corporate/classes`, {
        params: { school_id: schoolId, teacher_id: teacherId, year },
        withCredentials: true,
      });

      if (res.data?.status === "ok") {
        setClasses(res.data.classes || []);
      } else {
        setClassesError(res.data?.message || "Failed to load classes.");
      }
    } catch (e) {
      setClassesError(e?.response?.data?.message || e.message || "Failed to load classes.");
    } finally {
      setClassesLoading(false);
    }
  };


  // -----------------------------
  // POST: add class (creates verification code + upserts classconfigs + classcodelookup)
  // -----------------------------
  const handleAddClass = async () => {
    setAddClassStatus("");
    setAddClassError("");

    const raw = (newStudentClass || "").trim();
    if (!raw) {
      setAddClassError("Please enter a class name (e.g., 6A, 6 Endeavour, 1M).");
      return;
    }
    if (!schoolId || !teacherId || !year || !examLevel) {
      setAddClassError("Missing required fields (School/Teacher/Year/Exam Level).");
      return;
    }

    setAddClassLoading(true);
    try {
      const res = await axios.post(
        `/api/corporate/add-class`,
        {
          school_id: schoolId,
          teacher_id: teacherId,
          year,
          exam_level: examLevel,
          student_class: raw,
          // optional convenience (backend can also read from session)
          updated_by: (teacherEmail || "").toLowerCase(),
        },
        { withCredentials: true }
      );

      if (res.data?.ok) {
        setAddClassStatus(
          res.data?.student_verification_code
            ? `Class added. Verification code: ${res.data.student_verification_code}`
            : "Class added."
        );
        setNewStudentClass("");
        await fetchClasses();

        // Auto-select the newly added class if backend returns it
        const added = res.data?.student_class || raw;
        setClassId(added);
      } else {
        setAddClassError(res.data?.error || "Failed to add class.");
      }
    } catch (e) {
      setAddClassError(e?.response?.data?.error || e.message || "Failed to add class.");
    } finally {
      setAddClassLoading(false);
    }
  };

  useEffect(() => {
    setClassId("");
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, teacherId, year]);

  // -----------------------------
  // GET: current question file (for selected class/type)
  // -----------------------------
  const fetchCurrentFile = async () => {
    setLoadError("");
    setCurrentFile(null);

    if (!schoolId || !teacherId || !year || !classId || !typeOfWriting || !examLevel) return;

    setCurrentFileLoading(true);
    try {
      const res = await axios.get(`/api/corporate/questions/current`, {
        params: {
          school_id: schoolId,
          teacher_id: teacherId,
          year,
          student_class: classId,
          type_of_writing: typeOfWriting,
          exam_level: examLevel,
        },
        withCredentials: true,
      });

      if (res.data?.status === "ok" || res.data?.status === "success") {
        setCurrentFile(res.data.file || null);
      } else {
        setLoadError(res.data?.message || "Failed to load current file.");
      }
    } catch (e) {
      setLoadError(e?.response?.data?.message || e.message || "Failed to load current file.");
    } finally {
      setCurrentFileLoading(false);
    }
  };

  useEffect(() => {
    setGuidelinesStatus("");
    fetchCurrentFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, teacherId, year, classId, typeOfWriting, examLevel]);

  const fetchExistingOralTopics = async () => {
    setExistingOralTopicsStatus("");
    setExistingOralTopics([]);

    if (!schoolId || !teacherId || !year || !classId) {
      return;
    }

    setExistingOralTopicsLoading(true);
    try {
      const res = await axios.get(`/api/oral/topics`, {
        params: {
          school_id: schoolId,
          teacher_id: teacherId,
          year,
          student_class: classId,
          language_id: "english",
          topic_set_id: "custom",
        },
        withCredentials: true,
      });

      const payload = res.data?.data || res.data || {};
      const customStandards = payload?.standards || [];
      const topics = customStandards.flatMap((standard) => standard?.topics || []);

      setExistingOralTopics(topics);
      if (topics.length === 0) {
        setExistingOralTopicsStatus("No custom oral topics found for this class.");
      }
    } catch (e) {
      setExistingOralTopicsStatus(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Failed to load oral topics."
      );
    } finally {
      setExistingOralTopicsLoading(false);
    }
  };

  // -----------------------------
  // GET: class config (verification code) for selected class
  // -----------------------------
  const fetchClassConfig = async () => {
    setClassConfig(null);
    setClassConfigError("");

    if (!schoolId || !teacherId || !year || !classId || !examLevel) return;

    setClassConfigLoading(true);
    try {
      const res = await axios.get(`/api/corporate/classes/${encodeURIComponent(classId)}/config`, {
        params: {
          school_id: schoolId,
          teacher_id: teacherId,
          year,
          exam_level: examLevel,
        },
        withCredentials: true,
      });

      if (res.data?.status === "ok") {
        setClassConfig(res.data.config || null);
      } else {
        setClassConfigError(res.data?.message || "Failed to load class config.");
      }
    } catch (e) {
      setClassConfigError(e?.response?.data?.message || e.message || "Failed to load class config.");
    } finally {
      setClassConfigLoading(false);
    }
  };

  useEffect(() => {
    fetchClassConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, teacherId, year, classId, examLevel]);

  useEffect(() => {
    if (activeUploadTab === "oral") {
      fetchExistingOralTopics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUploadTab, schoolId, teacherId, year, classId]);

  // -----------------------------
  // POST: upload PDF
  // -----------------------------
  const handleUpload = async (e) => {
    e.preventDefault();
    setStatus("");
    setJobId("");
    setJobStatus("");
    setJobResult(null);
    clearPolling();

    if (!schoolId || !teacherId) {
      setStatus("Missing session context. Please log in again.");
      return;
    }
    if (!year || !classId || !typeOfWriting || !examLevel) {
      setStatus("Please select Year, Class, Type of Writing, and Exam Level first.");
      return;
    }
    if (!selectedFile) {
      setStatus("Please choose a PDF file first.");
      return;
    }

    try {
      setStatus("Uploading...");

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("school_id", schoolId);
      formData.append("teacher_id", teacherId);
      formData.append("year", year);
      formData.append("class", classId);
      formData.append("type_of_writing", typeOfWriting);
      formData.append("exam_level", examLevel);

      const res = await axios.post(`/api/corporate/teacher-upload-pdf`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      const jid = res.data?.job_id || "";
      setJobId(jid);

      if (jid) {
        setStatus("Upload queued. Processing...");
        startPolling(jid);
      } else {
        setStatus(res.data?.message || "Upload done.");
      }
    } catch (err) {
      setStatus(err?.response?.data?.message || err.message || "Upload failed.");
    }
  };

  // -----------------------------
  // POST: upsert additional grading guidelines (all questions for the paper)
  // -----------------------------
  const handleSaveGuidelines = async () => {
    setGuidelinesStatus("");

    if (!schoolId || !teacherId) {
      setGuidelinesStatus("Missing session context. Please log in again.");
      return;
    }
    if (!year || !classId || !typeOfWriting || !examLevel) {
      setGuidelinesStatus("Please select Year, Class, Type of Writing, and Exam Level first.");
      return;
    }

    try {
      setGuidelinesSaving(true);
      setGuidelinesStatus("Saving guidelines...");

      const res = await axios.post(
        `/api/corporate/grading-guidelines/upsert`,
        {
          school_id: schoolId,
          teacher_id: teacherId,
          year,
          student_class: classId,
          type_of_writing: typeOfWriting,
          exam_level: examLevel,
          // One blob/text chunk intended to cover ALL questions on the compo question page
          guidelines_text: customGuidelines,
        },
        { withCredentials: true }
      );

      setGuidelinesStatus(res.data?.message || "Guidelines saved.");
    } catch (e) {
      setGuidelinesStatus(e?.response?.data?.message || e.message || "Failed to save guidelines.");
    } finally {
      setGuidelinesSaving(false);
    }
  };

  const resetOralTopicForm = () => {
    setOralTopicTitle("");
    setOralPictureStimulus("");
    setOralPictureFile(null);
    setOralQuestion1("");
    setOralQuestion2("");
    setOralQuestion3("");
    setOralGradingGeneral("");
    setOralGradingQuestion1("");
    setOralGradingQuestion2("");
    setOralGradingQuestion3("");
  };

  const handleUploadOralTopic = async () => {
    setOralTopicStatus("");

    if (!schoolId) {
      setOralTopicStatus("School is required.");
      return;
    }
    if (!teacherId) {
      setOralTopicStatus("Teacher is required.");
      return;
    }
    if (!year) {
      setOralTopicStatus("Year is required.");
      return;
    }
    if (!classId) {
      setOralTopicStatus("Class is required.");
      return;
    }
    if (!oralTopicTitle.trim()) {
      setOralTopicStatus("Topic title is required.");
      return;
    }
    if (!oralPictureFile) {
      setOralTopicStatus("Picture file is required.");
      return;
    }
    if (!oralQuestion1.trim()) {
      setOralTopicStatus("Question 1 is required.");
      return;
    }
    if (!oralQuestion2.trim()) {
      setOralTopicStatus("Question 2 is required.");
      return;
    }
    if (!oralQuestion3.trim()) {
      setOralTopicStatus("Question 3 is required.");
      return;
    }

    try {
      setOralTopicUploading(true);
      setOralTopicStatus("Uploading...");

      const formData = new FormData();
      formData.append("file", oralPictureFile);
      formData.append("school_id", schoolId);
      formData.append("teacher_id", teacherId);
      formData.append("teacher_email", teacherEmail);
      formData.append("year", year);
      formData.append("student_class", classId);
      formData.append("topic_title", oralTopicTitle.trim());
      formData.append("picture_stimulus", oralPictureStimulus.trim());
      formData.append("question_1", oralQuestion1.trim());
      formData.append("question_2", oralQuestion2.trim());
      formData.append("question_3", oralQuestion3.trim());
      formData.append("grading_general", oralGradingGeneral.trim());
      formData.append("grading_question_1", oralGradingQuestion1.trim());
      formData.append("grading_question_2", oralGradingQuestion2.trim());
      formData.append("grading_question_3", oralGradingQuestion3.trim());

      const res = await axios.post(`/api/oral/custom-topics`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      const returnedTopicId =
        res.data?.data?.topic_id || res.data?.topic_id || "";
      setOralTopicStatus(
        returnedTopicId
          ? `Custom oral topic created. Topic ID: ${returnedTopicId}`
          : "Custom oral topic created."
      );
      resetOralTopicForm();
      fetchExistingOralTopics();
    } catch (e) {
      setOralTopicStatus(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Failed to create custom oral topic."
      );
    } finally {
      setOralTopicUploading(false);
    }
  };

  const handleDeactivateOralTopic = async (topicId) => {
    if (!schoolId || !teacherId || !year || !classId || !topicId) {
      setExistingOralTopicsStatus("Missing required fields to remove custom oral topic.");
      return;
    }

    const confirmed = window.confirm(
      "Remove this custom oral topic from the student topic list?"
    );
    if (!confirmed) {
      return;
    }

    try {
      setDeletingOralTopicId(topicId);
      await axios.delete(`/api/oral/custom-topics/${encodeURIComponent(topicId)}`, {
        data: {
          school_id: schoolId,
          teacher_id: teacherId,
          year,
          student_class: classId,
        },
        withCredentials: true,
      });

      setExistingOralTopicsStatus("Custom oral topic removed.");
      fetchExistingOralTopics();
    } catch (e) {
      setExistingOralTopicsStatus(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Failed to remove custom oral topic."
      );
    } finally {
      setDeletingOralTopicId("");
    }
  };

  // -----------------------------
  // Poll job status
  // -----------------------------
  const startPolling = (jid) => {
    clearPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`/api/check-job-status`, {
          params: { job_id: jid },
          withCredentials: true,
        });

        const st = r.data?.status || "processing";
        setJobStatus(st);

        if (st === "complete" || st === "failed" || st === "error") {
          clearPolling();
          setJobResult(r.data || null);
          setStatus(st === "complete" ? "Processing complete." : "Processing failed.");
          fetchCurrentFile();
          fetchClassConfig();
        }
      } catch {
        setJobStatus("processing");
      }
    }, 1500);
  };

  // -----------------------------
  // DELETE: remove current question file
  // -----------------------------
  const handleRemoveCurrent = async () => {
    setRemoveStatus("");
    if (!currentFile) return;

    const ok = window.confirm(
      "Remove the current question file? This will delete the PDF and related JSON for this class/type."
    );
    if (!ok) return;

    try {
      setRemoveStatus("Removing...");

      const res = await axios.delete(`/api/corporate/questions/current`, {
        data: {
          school_id: schoolId,
          teacher_id: teacherId,
          year,
          student_class: classId,
          type_of_writing: typeOfWriting,
          exam_level: examLevel,
        },
        withCredentials: true,
      });

      setRemoveStatus(res.data?.message || "Removed.");
      fetchCurrentFile();
    } catch (e) {
      setRemoveStatus(e?.response?.data?.message || e.message || "Remove failed.");
    }
  };

  // -----------------------------
  // Rotate class verification code
  // -----------------------------
  const handleRotateCode = async () => {
    setRotateStatus("");

    if (!schoolId || !teacherId || !year || !classId || !examLevel) {
      setRotateStatus("Missing required selection (Year/Class/Exam Level).");
      return;
    }

    try {
      setRotateStatus("Generating new code...");

      const studentClassLower = (classId || "").toLowerCase();

      const res = await axios.post(
        `/api/corporate/classes/${encodeURIComponent(studentClassLower)}/rotate-code`,
        {
          school_id: schoolId,
          teacher_id: teacherId,
          year,
          exam_level: examLevel,
          updated_by: (teacherEmail || "").toLowerCase(),
        },
        { withCredentials: true }
      );

      setRotateStatus(res.data?.message || "Code rotated.");
      fetchClassConfig();
    } catch (e) {
      setRotateStatus(e?.response?.data?.message || e.message || "Rotate failed.");
    }
  };

  useEffect(() => {
    return () => clearPolling();
  }, []);

  return (
    <div style={{ padding: 18, maxWidth: 980, margin: "0 auto" }}>
      <h2 style={{ margin: "6px 0 12px" }}>Teacher Upload (Question PDF)</h2>

      {/* Session info (no input fields) */}
      <div style={{ marginBottom: 12, padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
        <div style={{ fontSize: 14, opacity: 0.85 }}>
          <strong>School:</strong> {schoolId || "-"} &nbsp;|&nbsp; <strong>Teacher:</strong> {teacherId || "-"}
        </div>
        <div style={{ fontSize: 13, opacity: 0.7 }}>{teacherEmail ? `Signed in as ${teacherEmail}` : ""}</div>
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "grid", gap: 6 }}>
          <label>Exam Level *</label>
          <select value={examLevel} onChange={(e) => setExamLevel(e.target.value)}>
            {EXAM_LEVEL_OPTIONS.map((x) => (
              <option key={x.value} value={x.value}>
                {x.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "grid", gap: 6 }}>
          <label>Year *</label>
          <select value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">Select year</option>
            {YEAR_OPTIONS.map((y) => (
              <option key={y.value} value={y.value}>
                {y.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "grid", gap: 6 }}>
          <label>Class *</label>
          <select
            value={classId}
            onChange={(e) => setClassId(e.target.value)}
            disabled={classesLoading || !schoolId || !teacherId}
          >
            <option value="">
              {classesLoading ? "Loading..." : classesError ? "Failed to load classes" : "Select class"}
            </option>
            {classes.map((c) => (
              <option key={c.student_class} value={c.student_class}>
                {c.student_class}
              </option>
            ))}
          </select>
          {classesError ? <div style={{ color: "crimson" }}>{classesError}</div> : null}
        </div>

        <div style={{ display: "grid", gap: 8, padding: 10, border: "1px dashed #ddd", borderRadius: 8 }}>
          <div style={{ fontSize: 13, opacity: 0.75 }}>
            Add a class (one-way). Contact Admin to remove a Class.
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <input
              type="text"
              placeholder="e.g., 6A, 6 Articulate, 1M, etc"
              value={newStudentClass}
              onChange={(e) => setNewStudentClass(e.target.value)}
              style={{ flex: 1, padding: "8px 10px" }}
            />
            <button type="button" onClick={handleAddClass} disabled={addClassLoading}>
              {addClassLoading ? "Adding..." : "Add New Class"}
            </button>
          </div>
          {addClassError ? <div style={{ color: "crimson" }}>{addClassError}</div> : null}
          {addClassStatus ? <div style={{ opacity: 0.8 }}>{addClassStatus}</div> : null}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 18, marginBottom: 12 }}>
        <button
          type="button"
          style={tabButtonStyle(activeUploadTab === "writing")}
          onClick={() => setActiveUploadTab("writing")}
        >
          Essays
        </button>
        <button
          type="button"
          style={tabButtonStyle(activeUploadTab === "oral")}
          onClick={() => setActiveUploadTab("oral")}
        >
          Oral Exam
        </button>
      </div>

      {activeUploadTab === "writing" ? (
        <>
          <div style={{ border: "1px solid #ddd", borderRadius: 10, padding: 12 }}>
            <form onSubmit={handleUpload} style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gap: 6 }}>
                <label>Type of Writing *</label>
                <select value={typeOfWriting} onChange={(e) => setTypeOfWriting(e.target.value)}>
                  {TYPE_OF_WRITING_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label>Question PDF *</label>
                <input type="file" accept="application/pdf" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
              </div>

              <button
                type="submit"
                style={primaryActionButtonStyle}
                onMouseOver={handlePrimaryButtonMouseOver}
                onMouseOut={handlePrimaryButtonMouseOut}
              >
                Upload
              </button>

              <div style={{ display: "grid", gap: 6 }}>
                <label>Additional Custom Grading Guidelines:</label>
                <textarea
                  value={customGuidelines}
                  onChange={(e) => setCustomGuidelines(e.target.value)}
                  placeholder={"After uploading the Question PDF, if needed, paste any additional marking guidelines or additional custom rubrics here. Example:\nQ1: ...\nQ2: ...\nQ3: ...\n"}
                  rows={6}
                  style={{ padding: "10px 12px", resize: "vertical" }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 8,
                  }}
                >
                  <button
                    type="button"
                    onClick={handleSaveGuidelines}
                    disabled={guidelinesSaving}
                    style={primaryActionButtonStyle}
                    onMouseOver={handlePrimaryButtonMouseOver}
                    onMouseOut={handlePrimaryButtonMouseOut}
                  >
                    {guidelinesSaving ? "Saving..." : "Save Guidelines"}
                  </button>
                </div>

                {guidelinesStatus ? (
                  <div style={{ textAlign: "center", marginTop: 6, opacity: 0.8 }}>
                    {guidelinesStatus}
                  </div>
                ) : null}
              </div>

              {status ? <div style={{ marginTop: 6 }}>{status}</div> : null}

              {jobId ? (
                <div style={{ marginTop: 6, opacity: 0.8 }}>
                  Job ID: <code>{jobId}</code> &nbsp;|&nbsp; Status: <strong>{jobStatus || "queued"}</strong>
                </div>
              ) : null}

              {SHOW_DEBUG_JSON && jobResult ? (
                <pre style={{ marginTop: 10, padding: 10, background: "#f7f7f7", borderRadius: 8, overflowX: "auto" }}>
                  {JSON.stringify(jobResult, null, 2)}
                </pre>
              ) : null}
            </form>
          </div>

          <div style={{ marginTop: 22 }}>
            <h3 style={{ margin: "8px 0" }}>Current Question File</h3>

            {currentFileLoading ? (
              <div>Loading current file...</div>
            ) : loadError ? (
              <div style={{ color: "crimson" }}>{loadError}</div>
            ) : !currentFile ? (
              <div style={{ opacity: 0.7 }}>No current file found for this selection.</div>
            ) : (
              <div style={{ border: "1px solid #ddd", borderRadius: 10, padding: 12 }}>
                <div>
                  <strong>{currentFile.filename || "Question PDF"}</strong>
                </div>
                <div style={{ fontSize: 13, opacity: 0.75 }}>Uploaded: {formatUploadedAt(currentFile.uploaded_at)}</div>

                <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
                  {currentFile.download_url ? (
                    <a href={currentFile.download_url} target="_blank" rel="noreferrer">
                      Download
                    </a>
                  ) : null}
                  <button type="button" onClick={handleRemoveCurrent}>
                    Remove
                  </button>
                  {removeStatus ? <span style={{ opacity: 0.8 }}>{removeStatus}</span> : null}
                </div>
              </div>
            )}
          </div>
        </>
      ) : null}

      {activeUploadTab === "oral" ? (
        <div style={{ marginTop: 22 }}>
          <div style={{ display: "grid", gap: 10, border: "1px solid #ddd", borderRadius: 10, padding: 12 }}>
            <h3 style={{ margin: "8px 0" }}>Oral Exam Custom Topic</h3>
            <div style={{ display: "grid", gap: 6 }}>
              <label>Topic Title *</label>
              <input
                type="text"
                value={oralTopicTitle}
                onChange={(e) => setOralTopicTitle(e.target.value)}
                style={{ padding: "8px 10px" }}
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <label>Picture Stimulus Description</label>
              <textarea
                value={oralPictureStimulus}
                onChange={(e) => setOralPictureStimulus(e.target.value)}
                rows={4}
                style={{ padding: "10px 12px", resize: "vertical" }}
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <label>Picture Upload *</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setOralPictureFile(e.target.files?.[0] || null)}
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <label>Question 1 *</label>
              <textarea
                value={oralQuestion1}
                onChange={(e) => setOralQuestion1(e.target.value)}
                rows={3}
                style={{ padding: "10px 12px", resize: "vertical" }}
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <label>Question 2 *</label>
              <textarea
                value={oralQuestion2}
                onChange={(e) => setOralQuestion2(e.target.value)}
                rows={3}
                style={{ padding: "10px 12px", resize: "vertical" }}
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <label>Question 3 *</label>
              <textarea
                value={oralQuestion3}
                onChange={(e) => setOralQuestion3(e.target.value)}
                rows={3}
                style={{ padding: "10px 12px", resize: "vertical" }}
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <label>General Grading Guidelines</label>
              <textarea
                value={oralGradingGeneral}
                onChange={(e) => setOralGradingGeneral(e.target.value)}
                rows={4}
                style={{ padding: "10px 12px", resize: "vertical" }}
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <label>Question 1 Grading Guidelines</label>
              <textarea
                value={oralGradingQuestion1}
                onChange={(e) => setOralGradingQuestion1(e.target.value)}
                rows={3}
                style={{ padding: "10px 12px", resize: "vertical" }}
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <label>Question 2 Grading Guidelines</label>
              <textarea
                value={oralGradingQuestion2}
                onChange={(e) => setOralGradingQuestion2(e.target.value)}
                rows={3}
                style={{ padding: "10px 12px", resize: "vertical" }}
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <label>Question 3 Grading Guidelines</label>
              <textarea
                value={oralGradingQuestion3}
                onChange={(e) => setOralGradingQuestion3(e.target.value)}
                rows={3}
                style={{ padding: "10px 12px", resize: "vertical" }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
              <button
                type="button"
                onClick={handleUploadOralTopic}
                disabled={oralTopicUploading}
                style={primaryActionButtonStyle}
                onMouseOver={handlePrimaryButtonMouseOver}
                onMouseOut={handlePrimaryButtonMouseOut}
              >
                {oralTopicUploading ? "Uploading..." : "Upload Custom Oral Topic"}
              </button>
            </div>

            {oralTopicStatus ? (
              <div style={{ textAlign: "center", marginTop: 6, opacity: 0.85 }}>
                {oralTopicStatus}
              </div>
            ) : null}
          </div>

          <div style={{ marginTop: 22 }}>
            <h3 style={{ margin: "8px 0" }}>Existing Oral Topics</h3>

            {existingOralTopicsLoading ? (
              <div>Loading oral topics...</div>
            ) : existingOralTopics.length === 0 ? (
              <div style={{ opacity: 0.75 }}>
                {existingOralTopicsStatus || "No custom oral topics found for this class."}
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {existingOralTopics.map((topic, index) => (
                  <div
                    key={topic.topic_id || topic.topic_title || `oral_topic_${index}`}
                    style={{ border: "1px solid #ddd", borderRadius: 10, padding: 12 }}
                  >
                    <strong>{topic.topic_title || "Untitled topic"}</strong>
                    {topic.picture_stimulus_url ? (
                      <img
                        src={topic.picture_stimulus_url}
                        alt={topic.topic_title || "Oral topic"}
                        style={{
                          maxWidth: 220,
                          display: "block",
                          marginTop: 8,
                          borderRadius: 8,
                        }}
                      />
                    ) : null}
                    <p>{topic.picture_stimulus || "No picture stimulus description."}</p>
                    <ol>
                      <li>{topic.questions?.question_1 || "-"}</li>
                      <li>{topic.questions?.question_2 || "-"}</li>
                      <li>{topic.questions?.question_3 || "-"}</li>
                    </ol>
                    <button
                      type="button"
                      onClick={() => handleDeactivateOralTopic(topic.topic_id)}
                      disabled={deletingOralTopicId === topic.topic_id}
                      style={{ backgroundColor: "crimson", color: "white" }}
                    >
                      {deletingOralTopicId === topic.topic_id ? "Removing..." : "Remove"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 22 }}>
        <h3 style={{ margin: "8px 0" }}>Student Verification Code</h3>

        {classConfigLoading ? (
          <div>Loading code...</div>
        ) : classConfigError ? (
          <div style={{ color: "crimson" }}>{classConfigError}</div>
        ) : !classId ? (
          <div style={{ opacity: 0.7 }}>Select Year, Class, and Exam Level to view code.</div>
        ) : (
          <div style={{ border: "1px solid #ddd", borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 2 }}>
              {classConfig?.student_verification_code || "— — — — — —"}
            </div>
            <div style={{ fontSize: 13, opacity: 0.75, marginTop: 6 }}>Active: {String(classConfig?.is_active ?? "")}</div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 10,
              }}
            >
              <button
                type="button"
                onClick={handleRotateCode}
                disabled={classConfigLoading}
                style={{ padding: "10px 18px" }}
              >
                Regenerate Verification Code
              </button>
            </div>
            {rotateStatus ? (
              <div style={{ textAlign: "center", marginTop: 6 }}>{rotateStatus}</div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default TeacherPdfUploader;
